import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from '@/lib/http';
import { XCircle } from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

interface SharedReport {
  id: string;
  client_name: string;
  claimant_name: string;
  business_address: string;
  business_type: string;
  event_type: string;
  event_start_date: string;
  event_end_date: string;
  coverage_year: number;
  result: {
    final_claim_amount: number;
    causation_test_passed: boolean;
    [key: string]: any;
  };
  // Canonical sworn net from the share payload (engine_outputs policy_deductions.net_claim).
  // Headline figure; result.final_claim_amount is the stale snapshot fallback only.
  net_supported_claim?: number | null;
  legacy_final_claim_amount?: number | null;
  calculation_mode: string;
  created_at: string;
  financial_summary: {
    years: string[];
    has_reconstructed_data: boolean;
  };
  _share?: {
    watermark: string;
    expires_at: string;
    view_count: number;
    read_only: boolean;
  };
}

function UnavailableCard() {
  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-base)' }}>
      <div
        className="max-w-md text-center"
        style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 32 }}
      >
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
          style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
        >
          <span className="text-3xl">&#x1F512;</span>
        </div>
        <h1 className="cs-h2 mb-2">Report Unavailable</h1>
        <p className="cs-meta mb-6">This shared report is unavailable or the link has expired</p>
        <Link
          to="/"
          className="cs-chip inline-block"
          style={{ padding: '8px 24px', background: 'var(--color-brand)', color: 'var(--color-on-brand)', borderRadius: 8 }}
        >
          Go to Homepage
        </Link>
      </div>
    </div>
  );
}

export default function PublicShareView() {
  const { shareToken } = useParams<{ shareToken: string }>();
  const [report, setReport] = useState<SharedReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadSharedReport = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/v1/collaboration/public/${shareToken}`);
        setReport(response.data);
      } catch (err: any) {
        if (err.response?.status === 404) {
          setError('Share link not found. It may have been revoked.');
        } else if (err.response?.status === 410) {
          setError('This share link has expired.');
        } else {
          setError('Failed to load shared report.');
        }
      } finally {
        setLoading(false);
      }
    };

    if (shareToken) {
      loadSharedReport();
    }
  }, [shareToken]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-base)' }}>
        <div className="text-center">
          <div
            className="animate-spin rounded-full h-12 w-12 mx-auto mb-4"
            style={{ borderBottom: '4px solid var(--color-brand)' }}
          ></div>
          <p className="cs-meta">Loading shared report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-base)' }}>
        <div
          className="max-w-md text-center"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 32 }}
        >
          <div
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}
          >
            <XCircle className="w-8 h-8" style={{ color: 'var(--color-needs-human-text)' }} aria-hidden="true" />
          </div>
          <h1 className="cs-h2 mb-2">Link Unavailable</h1>
          <p className="cs-meta mb-6">{error}</p>
          <Link
            to="/"
            className="cs-chip inline-block"
            style={{ padding: '8px 24px', background: 'var(--color-brand)', color: 'var(--color-on-brand)', borderRadius: 8 }}
          >
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  if (!report || !report._share) return <UnavailableCard />;

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg-base)' }}>
      {/* Watermark Header */}
      {report._share && (
        <div
          className="cs-chip py-2 px-4 text-center"
          style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}
        >
          <span className="mr-2">&#x1F512;</span>
          {report._share.watermark} • View #{report._share.view_count} •
          Expires {new Date(report._share.expires_at).toLocaleDateString()}
        </div>
      )}

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div
          className="mb-6"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="cs-display">{report.client_name}</h1>
                <p className="cs-meta">Business Interruption Claim Report</p>
              </div>
            </div>
            <span
              className="cs-chip"
              style={{ padding: '4px 12px', background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 9999 }}
            >
              READ-ONLY
            </span>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="cs-label">Claimant</p>
              <p className="cs-body" style={{ marginTop: 4 }}>{report.claimant_name || 'N/A'}</p>
            </div>
            <div>
              <p className="cs-label">Business Type</p>
              <p className="cs-body" style={{ marginTop: 4 }}>{report.business_type || 'N/A'}</p>
            </div>
            <div>
              <p className="cs-label">Event Type</p>
              <p className="cs-body" style={{ marginTop: 4 }}>{report.event_type || 'N/A'}</p>
            </div>
            <div>
              <p className="cs-label">Coverage Year</p>
              <p className="cs-body" style={{ marginTop: 4 }}>{report.coverage_year || 'N/A'}</p>
            </div>
          </div>
        </div>

        {/* Claim Amount */}
        {report.result && (
          <div
            className="mb-6"
            style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}
          >
            <h2 className="cs-h2 mb-4">Calculation Result</h2>

            <div
              className="text-center"
              style={{ background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}
            >
              <p className="cs-label mb-2">Total Calculated Claim</p>
              <p className="cs-figure-lg">
                <FinancialValue value={report.net_supported_claim ?? report.result.final_claim_amount} />
              </p>

              <div className="mt-4 flex items-center justify-center gap-2">
                {report.result.causation_test_passed ? (
                  <span
                    className="cs-chip"
                    style={{ padding: '4px 12px', background: 'transparent', color: 'var(--color-verified)', borderRadius: 9999 }}
                  >
                    Causation Test Passed
                  </span>
                ) : (
                  <span
                    className="cs-chip"
                    style={{ padding: '4px 12px', background: 'var(--color-bg-elevated)', color: 'var(--color-text-muted)', border: '1px solid var(--color-border)', borderRadius: 9999 }}
                  >
                    ❌ Causation Test Failed
                  </span>
                )}
                <span
                  className="cs-chip"
                  style={{ padding: '4px 12px', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)', borderRadius: 9999 }}
                >
                  {report.calculation_mode === 'future_closure' ? 'Future Closure' : 'Standard BI'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Event Details */}
        <div
          className="mb-6"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}
        >
          <h2 className="cs-h2 mb-4">Event Details</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div style={{ background: 'var(--color-bg-elevated)', borderRadius: 12, padding: 16 }}>
              <p className="cs-label">Event Start Date</p>
              <p className="cs-body" style={{ marginTop: 4 }}>{report.event_start_date || 'Not specified'}</p>
            </div>
            <div style={{ background: 'var(--color-bg-elevated)', borderRadius: 12, padding: 16 }}>
              <p className="cs-label">Event End Date</p>
              <p className="cs-body" style={{ marginTop: 4 }}>{report.event_end_date || 'Not specified'}</p>
            </div>
            <div style={{ background: 'var(--color-bg-elevated)', borderRadius: 12, padding: 16 }}>
              <p className="cs-label">Business Address</p>
              <p className="cs-body" style={{ marginTop: 4 }}>{report.business_address || 'Not specified'}</p>
            </div>
            <div style={{ background: 'var(--color-bg-elevated)', borderRadius: 12, padding: 16 }}>
              <p className="cs-label">Report Created</p>
              <p className="cs-body" style={{ marginTop: 4 }}>{new Date(report.created_at).toLocaleDateString()}</p>
            </div>
          </div>
        </div>

        {/* Financial Data Summary */}
        <div
          className="mb-6"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}
        >
          <h2 className="cs-h2 mb-4">Financial Data</h2>

          <div className="flex flex-wrap gap-2 mb-4">
            {report.financial_summary?.years?.map((year) => (
              <span
                key={year}
                className="cs-chip"
                style={{ padding: '4px 12px', background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)', border: '1px solid var(--color-border)', borderRadius: 9999 }}
              >
                {year.replace('data_', '')}
              </span>
            ))}
          </div>

          {report.financial_summary?.has_reconstructed_data && (
            <div
              className="cs-body"
              style={{ padding: 12, background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 8, color: 'var(--color-needs-human-text)' }}
            >
              <strong>Note:</strong> Financial data was reconstructed from bank/credit card statements.
              All estimates are documented in the full report.
            </div>
          )}
        </div>

        {/* PDF Download */}
        <div
          className="mb-6"
          style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}
        >
          <h2 className="cs-h2 mb-4">Download Report</h2>

          <a
            href={`${BACKEND_URL}/api/v1/collaboration/public/${shareToken}/pdf`}
            className="cs-chip inline-flex items-center gap-2 transition-colors"
            style={{ padding: '12px 24px', background: 'var(--color-brand)', color: 'var(--color-on-brand)', borderRadius: 8 }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download PDF (Read-Only)
          </a>

          {report._share && (
            <p className="cs-meta" style={{ marginTop: 8 }}>
              PDF includes watermark: &ldquo;{report._share.watermark}&rdquo;
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="cs-meta text-center">
          <p>This is a read-only shared view. To make changes, contact the report owner.</p>
          <p className="mt-2">
            Powered by <span style={{ fontWeight: 600, color: 'var(--color-needs-human-text)' }}>DisruptionGenius</span>
          </p>
        </div>
      </div>
    </div>
  );
}
